import { Space } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ProjectConversationId, ProjectId, User } from 'api/models';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { useProjectDetail } from 'data/projects';
import { useAccount } from 'hooks/use-account';
import { useInAndNotInProjectUsers } from 'hooks/use-assignee-options';
import { useAddMembersProjectConversationsMutation } from 'queries/project-wide-conversation';
import { useActiveProjectConversation } from '../../../../proj-conversation-hooks';
import { SelectUserDropdown } from '../../../select-user-dropdown';
import {
  Actions,
  AutoAddOption,
  Checkbox,
  MemberAddWrapper,
  Radio,
  UserSelectionWrapper,
} from './styles';

interface AddMembersFormProps {
  onSuccess: () => void;
  projectId: ProjectId;
  activeConversationId: ProjectConversationId;
}

enum AddMembersFormItems {
  AddFutureMembersToChannel = 'addFutureMemberToChannel',
}

interface AddMembersFormPayload {
  [AddMembersFormItems.AddFutureMembersToChannel]?: boolean;
}

export const AddMembersForm: FC<AddMembersFormProps> = ({
  onSuccess,
  projectId,
  activeConversationId,
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [addAllMembers, setAddAllMembers] = useState(false);
  const initialValues: AddMembersFormPayload = {
    addFutureMemberToChannel: false,
  };
  const activeConversation = useActiveProjectConversation();
  const users = useInAndNotInProjectUsers({
    projectId,
    isPrivate: activeConversation?.private,
  });
  const { data: project } = useProjectDetail(projectId);
  const { accountName, company: vendorCompany } = useAccount();
  const allUsers = [...users.inProjectUsers, ...users.notInProjectUsers];
  const currentMembers = activeConversation?.members
    ? activeConversation.members.map((member) => member.user.userId)
    : [];

  const nonMembers = allUsers.filter(
    (user) => !currentMembers.includes(user.userId),
  );

  const { mutate: addMembersToConversationMuatate, isLoading: isMemberAdding } =
    useAddMembersProjectConversationsMutation(projectId, activeConversationId);

  const onSubmit = () => {
    const formData = form.getFieldsValue(true);
    const members = addAllMembers ? users.inProjectUsers : selectedUsers;
    const membersPayload = members.map((member) => {
      return {
        user: member,
      };
    });

    addMembersToConversationMuatate(
      {
        addAllMembersToConversation: formData.addFutureMemberToChannel,
        members: membersPayload,
      },
      {
        onSuccess: () => {
          setSelectedUsers([]);
          setAddAllMembers(false);
          if (onSuccess) {
            onSuccess();
          }
        },
      },
    );
  };

  const handleOnChange = (value: User[]) => {
    setSelectedUsers(value || []);
  };

  const onAddMembersTypeChange = (e) => {
    setAddAllMembers(e.target.value);
  };

  const projectName = project?.projectName;
  const customerName = project?.customer.companyName;
  const vendorName = accountName ?? vendorCompany?.companyName;

  return (
    <Form
      form={form}
      name="addConversationMembersForm"
      autoComplete="off"
      initialValues={initialValues}
      preserve={false}
      layout="vertical"
      onFinish={onSubmit}
    >
      <MemberAddWrapper>
        <Radio.Group value={addAllMembers} onChange={onAddMembersTypeChange}>
          <Space direction="vertical">
            <Radio defaultChecked value>
              {activeConversation?.private ? (
                <FormattedMessage
                  id="projects.conversations.form.channel.members_add_all_private"
                  values={{
                    projectName,
                    vendorName,
                    projectMemberCount: users.inProjectUsers.length,
                    b: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="projects.conversations.form.channel.members_add_all"
                  values={{
                    projectName,
                    customerName,
                    vendorName,
                    projectMemberCount: users.inProjectUsers.length,
                    b: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              )}
            </Radio>
            <Radio value={false}>
              <FormattedMessage id="projects.conversations.form.channel.members_add_specific" />
            </Radio>
          </Space>
        </Radio.Group>
      </MemberAddWrapper>
      <UserSelectionWrapper>
        <SelectUserDropdown
          placeholder={intl.formatMessage({
            id: 'projects.conversations.form.channel.members_search',
          })}
          options={nonMembers}
          isDisabled={isMemberAdding || addAllMembers}
          isLoading={isMemberAdding}
          projectId={projectId}
          selectedUsers={selectedUsers}
          onChange={handleOnChange}
        />
      </UserSelectionWrapper>
      <Form.Item
        name={AddMembersFormItems.AddFutureMembersToChannel}
        valuePropName="checked"
      >
        <AutoAddOption>
          <Checkbox data-cy="projects.conversations.add_new.add_people.auto_add_people">
            <FormattedMessage
              id="projects.conversations.form.channel.members_auto_add"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
                projectName,
              }}
            />
          </Checkbox>
        </AutoAddOption>
      </Form.Item>
      <Form.Item>
        <Actions>
          <Button
            type="primary"
            htmlType="submit"
            loading={isMemberAdding}
            data-cy="projects.conversations.add_new.add_people.done"
          >
            <FormattedMessage id="done" />
          </Button>
        </Actions>
      </Form.Item>
    </Form>
  );
};
